import { useQuery } from 'react-query';
import { EDD_PRIORITIZE_TYPE } from '@pperform/pp-constants/widgetConfigs';
import { EDD_V2_SOURCE } from '@pperform/pp-constants/parcel';
import {
  getDisplayUserExpectedDelivery,
  getDisplayCarrierExpectedDelivery,
} from '@pperform/pp-utils/parcelOrg';
import { getShipmentEddBySource } from 'components/EstimatedDeliveryDate/services/edd.service';
import type Shipment from 'interfaces/PmParcel';
import { EddSource } from 'interfaces/edd.interface';

interface UseEddPriorityParams {
  parcel: Shipment;
  eddPriorityOrder: string[];
  orgId: number;
  /** Pre-computed gate: when false, skip fetching entirely and return null */
  isEddEligible: boolean;
}

interface UseEddPriorityReturn {
  prioritizedEdd: EddSource | null;
  isLoading: boolean;
}

// Resolves one EDD source — sync sources return immediately, PREDICT_EDD hits the API.
const fetchEddBySource = async (
  source: string,
  parcel: Shipment,
  orgId: number,
): Promise<EddSource | null> => {
  switch (source) {
    case EDD_PRIORITIZE_TYPE.CUSTOMER_EDD:
      return getDisplayUserExpectedDelivery(parcel);
    case EDD_PRIORITIZE_TYPE.CARRIER_EDD:
      return getDisplayCarrierExpectedDelivery(parcel);
    case EDD_PRIORITIZE_TYPE.PREDICT_EDD:
      return getShipmentEddBySource(
        orgId,
        parcel?.id,
        EDD_V2_SOURCE.PREDICT_ENGINE,
      );
    default:
      return null;
  }
};

/**
 * Walks `eddPriorityOrder` and returns the first source with a valid `first` date.
 * Skips everything when `isEddEligible` is false.
 * Loading state is only surfaced while the query is in-flight (no cached data yet).
 */
export const useEddPriority = ({
  parcel,
  eddPriorityOrder,
  orgId,
  isEddEligible,
}: UseEddPriorityParams): UseEddPriorityReturn => {
  const { data, isLoading } = useQuery(
    ['prioritized-edd', parcel?.id, eddPriorityOrder],
    async () => {
      // eslint-disable-next-line no-restricted-syntax
      for (const source of eddPriorityOrder) {
        // Sequential iteration is intentional: each source is a fallback for the previous
        try {
          // eslint-disable-next-line no-await-in-loop
          const edd = await fetchEddBySource(source, parcel, orgId);
          if (edd?.first) return edd;
        } catch {
          // fallthrough to next source
        }
      }
      return null;
    },
    {
      enabled: isEddEligible,
      staleTime: 10 * 60 * 1000, // 10 min
      cacheTime: 30 * 60 * 1000, // 30 min
    },
  );

  return {
    prioritizedEdd: data ?? null,
    isLoading,
  };
};
